import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Camera, X } from 'lucide-react';

function ForgotPasswordModal({ onClose }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [devModeLink, setDevModeLink] = useState('');
  const { forgotPassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await forgotPassword(email);
    setLoading(false);

    if (result.success) {
      setSuccess(true);
      // Check if dev mode response
      if (result.data?.dev_mode && result.data?.reset_link) {
        setDevModeLink(result.data.reset_link);
      }
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-surface border border-border rounded-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-serif font-bold text-foreground">Forgot Password</h2>
          <button onClick={onClose} className="text-text-muted hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!success ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm text-text-secondary">
              Enter your email address and we&apos;ll send you a link to reset your password.
            </p>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground placeholder-text-muted focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
                placeholder="you@example.com"
              />
            </div>

            {error && (
              <div className="text-alert text-sm bg-alert/10 border border-alert/20 rounded-lg p-3">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-surface-raised text-foreground hover:bg-surface-raised/80 font-medium rounded-lg px-4 py-2.5 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-primary text-background hover:bg-primary/90 font-medium rounded-lg px-4 py-2.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="text-success text-sm bg-success/10 border border-success/20 rounded-lg p-3">
              {devModeLink ? 'Reset link generated!' : 'If an account exists with that email, a reset link has been sent.'}
            </div>

            {devModeLink && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 space-y-2">
                <p className="text-xs text-amber-500 font-medium">⚠️ Email delivery not yet configured — use this test link:</p>
                <a
                  href={devModeLink}
                  className="text-sm text-primary hover:text-primary/80 underline break-all"
                >
                  {devModeLink}
                </a>
              </div>
            )}

            <button
              onClick={onClose}
              className="w-full bg-primary text-background hover:bg-primary/90 font-medium rounded-lg px-4 py-2.5 transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AuthScreen() {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  // Navigate to dashboard when user is authenticated
  useEffect(() => {
    if (user && user !== false) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let result;
      if (mode === 'login') {
        result = await login(email, password);
      } else {
        if (!name.trim()) {
          setError('Name is required');
          setLoading(false);
          return;
        }
        result = await register(email, password, name);
      }

      if (!result.success) {
        setError(result.error);
        setLoading(false);
      }
      // Don't navigate here - let useEffect handle it when user state updates
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-2xl mb-4">
            <Camera className="w-8 h-8 text-background" />
          </div>
          <h1 className="text-3xl font-serif font-bold text-foreground mb-2" data-testid="auth-title">
            Photog Bizkit
          </h1>
          <p className="text-text-secondary">Your photography business, in your pocket.</p>
        </div>

        <div className="bg-surface border border-border rounded-xl p-6">
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                mode === 'login'
                  ? 'bg-primary text-background'
                  : 'text-text-secondary hover:text-foreground hover:bg-surface-raised'
              }`}
              data-testid="auth-mode-login"
            >
              Sign In
            </button>
            <button
              onClick={() => setMode('register')}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                mode === 'register'
                  ? 'bg-primary text-background'
                  : 'text-text-secondary hover:text-foreground hover:bg-surface-raised'
              }`}
              data-testid="auth-mode-register"
            >
              Create Account
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground placeholder-text-muted focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
                  placeholder="Your name"
                  data-testid="auth-input-name"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Email
              </label>
              <input
                type="email"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onInput={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground placeholder-text-muted focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
                placeholder="you@example.com"
                data-testid="auth-input-email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Password
              </label>
              <input
                type="password"
                name={mode === 'login' ? 'current-password' : 'new-password'}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onInput={(e) => setPassword(e.target.value)}
                required
                className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground placeholder-text-muted focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
                placeholder="••••••••"
                data-testid="auth-input-password"
              />
              {mode === 'login' && (
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-primary hover:text-primary/80 mt-1.5 transition-colors"
                >
                  Forgot password?
                </button>
              )}
            </div>

            {error && (
              <div className="text-alert text-sm bg-alert/10 border border-alert/20 rounded-lg p-3" data-testid="auth-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-background hover:bg-primary/90 font-medium rounded-lg px-4 py-2.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="auth-submit-button"
            >
              {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
            </button>
          </form>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <ForgotPasswordModal 
          onClose={() => setShowForgotPassword(false)} 
        />
      )}
    </div>
  );
}
